// <purpose>THE binding between a CLASSIFIED-ALONGSIDE attribute as the tenant declares it and the
// two places the frontend meets it: the per-ticket wire FIELD its value arrives in, and the list
// QUERY PARAM that filters on it (wim vocabulary v7, 2026-09-13).
//
// Why a table at all, when the vocabulary already serves the attribute list: a served DECLARATION
// is enough to render a pill or a filter section (both read it by name, and neither needs to know
// what the attribute means), but it is NOT enough to READ a value off a ticket or to SEND a filter
// to the backend — those need the DTO field and the param the backend actually accepts. Those two
// facts are a contract between this frontend and core, not something a tenant declares, so they
// live here: three lines to bind a new attribute, in one file, instead of a spelling repeated
// across the row, the header, the panel, two hooks and two pages.
//
// The table's ORDER is the pill order on every surface (see TicketAttributePills, shared.tsx): a
// fixed slot sequence is what keeps a row from re-arranging itself as values arrive.</purpose>
// <dependencies>TicketAttributeValues (types.ts — the DTO field pair itself is declared with the
// DTOs that carry it, and this module binds to it rather than re-spelling it).</dependencies>
// <constraints>Spells NO face and NO label: a value's word comes from useCategoryMeta().attributeFace
// and an attribute's German noun from its served labelDe — this module is names and fields only. A
// tenant may declare attributes this table does not bind (the reminder flag): they are not
// an error, they simply have no per-ticket column and no list param, so attributeFilterParams
// ignores them rather than inventing one.</constraints>
import type { TicketAttributeValues } from '../types';

/** One bound attribute: what the vocabulary calls it, and which field/param carries its value. */
export interface TicketAttributeBinding {
  /**
   * The SERVED attribute name — vocabulary `attributes[].name`, snake_case. This is the key every
   * declaration/face accessor takes (cm.attribute / cm.attributeFace) AND the key an
   * attributeFilter record is keyed by, so a filter selection never has to be re-keyed on its way
   * from a bubble to a section heading.
   */
  name: string;
  /**
   * The camelCase field the value arrives in on a list item, a detail ticket and a triage record —
   * and, deliberately the same spelling, the list query param that filters on it.
   */
  field: keyof TicketAttributeValues;
}

/**
 * The bound attributes, IN PILL ORDER. Commodity first: it is the coarser fact and the one a clerk
 * scanning a list sorts by eye, and the sender kind qualifies it.
 */
export const TICKET_ATTRIBUTES: readonly TicketAttributeBinding[] = [
  { name: 'sparte', field: 'sparte' },
  { name: 'sender_kind', field: 'senderKind' },
];

/** A filter selection, keyed by SERVED attribute name — the shape both list pages hold as state. */
export type AttributeFilter = Record<string, string[]>;

/** The list params the selection translates into (mirrors smarttix.getActivePage/getArchivedPage). */
export type AttributeParams = Partial<Record<keyof TicketAttributeValues, string[]>>;

/**
 * The snake_case -> camelCase translation, in ONE place. An empty selection yields no key at all:
 * every list param this app sends is omitted rather than sent empty, and an empty `sparte=` would
 * read to the backend as a filter on nothing.
 */
export function attributeFilterParams(filter: AttributeFilter): AttributeParams {
  const params: AttributeParams = {};
  for (const binding of TICKET_ATTRIBUTES) {
    const values = filter[binding.name];
    if (values && values.length > 0) params[binding.field] = values;
  }
  return params;
}

/**
 * One attribute's selection, replaced — and PRUNED when it empties, so an untouched record and one
 * whose last bubble was just deselected are the same object shape. Both list pages hold this state
 * and both would otherwise spell the same five lines.
 */
export function withAttributeValues(filter: AttributeFilter, name: string, values: string[]): AttributeFilter {
  const next: AttributeFilter = { ...filter };
  if (values.length > 0) next[name] = values;
  else delete next[name];
  return next;
}

/** Whether any attribute is filtered — the pages' hasActiveFilters term. */
export const hasAttributeFilter = (filter: AttributeFilter): boolean =>
  Object.values(filter).some(values => values.length > 0);
